import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import databaseService from '../../services/databaseService';
import MaterialIcon from '../MaterialIcon';
import logger from '../../utils/logger';
import './PriorityTagsManager.css';

/**
 * Priority Tags Manager Component
 * Allows admin to set priority tags that will sort matching listings
 * to the top of the homepage
 */
function PriorityTagsManager() {
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Load all tags and current priority tags on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load all available tags and sort alphabetically
      const tagsData = await databaseService.getAllTags();
      const sortedTags = tagsData.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      setAllTags(sortedTags);

      // Load current priority tags from config
      const priorityTags = await databaseService.getHomepageConfig('priority_tags');
      if (priorityTags && Array.isArray(priorityTags)) {
        setSelectedTags(priorityTags);
      }

      logger.log('📌 Loaded tags:', {
        all: sortedTags.length,
        selected: priorityTags?.length || 0,
      });
    } catch (error) {
      console.error('Error loading tags:', error);
      setMessage({ type: 'error', text: 'Failed to load tags' });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTags = allTags.filter(
    (tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()) && !selectedTags.includes(tag)
  );

  const handleTagSelect = (tag) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
      setSearchTerm('');
    }
  };

  const handleTagRemove = (tag) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      await databaseService.saveHomepageConfig('priority_tags', selectedTags);

      logger.log('✅ Saved priority tags:', selectedTags);
      setMessage({
        type: 'success',
        text: `Successfully saved ${selectedTags.length} priority tag${selectedTags.length !== 1 ? 's' : ''}`,
      });
    } catch (error) {
      console.error('Error saving priority tags:', error);
      setMessage({ type: 'error', text: 'Failed to save priority tags' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      setSelectedTags([]);
      await databaseService.saveHomepageConfig('priority_tags', []);

      logger.log('✅ Cleared priority tags');
      setMessage({ type: 'success', text: 'Priority tags cleared' });
    } catch (error) {
      console.error('Error clearing priority tags:', error);
      setMessage({ type: 'error', text: 'Failed to clear priority tags' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="priority-tags-manager">
        <div className="loading-state">
          <MaterialIcon icon="hourglass_empty" size={24} />
          <p>Loading tags...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="priority-tags-manager">
      {/* Selected Tags */}
      <div className="selected-tags-section">
        <h3>
          <MaterialIcon icon="label" size={18} />
          Selected Priority Tags ({selectedTags.length})
        </h3>
        {selectedTags.length === 0 ? (
          <p className="empty-message">
            No priority tags selected. Tags below to prioritize them on the homepage.
          </p>
        ) : (
          <div className="tags-grid">
            {selectedTags.map((tag) => (
              <div key={tag} className="tag-chip selected">
                <span>{tag}</span>
                <button
                  className="remove-btn"
                  onClick={() => handleTagRemove(tag)}
                  aria-label={`Remove ${tag}`}
                >
                  <MaterialIcon icon="close" size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search/Filter Input */}
      <div className="search-section">
        <div className="search-input-wrapper">
          <MaterialIcon icon="search" size={20} />
          <input
            type="text"
            className="search-input"
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="clear-search-btn"
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
            >
              <MaterialIcon icon="close" size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Available Tags */}
      <div className="available-tags-section">
        <h3>
          <MaterialIcon icon="local_offer" size={18} />
          Available Tags ({filteredTags.length})
        </h3>
        {filteredTags.length === 0 ? (
          <p className="empty-message">
            {searchTerm ? 'No tags match your search.' : 'All tags are already selected.'}
          </p>
        ) : (
          <div className="tags-grid">
            {filteredTags.map((tag) => (
              <button key={tag} className="tag-chip available" onClick={() => handleTagSelect(tag)}>
                <MaterialIcon icon="add" size={16} />
                <span>{tag}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="actions-section">
        <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          <MaterialIcon icon="save" size={16} />
          {isSaving ? 'Saving...' : 'Save Priority Tags'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={handleClear}
          disabled={isSaving || selectedTags.length === 0}
        >
          <MaterialIcon icon="delete" size={16} />
          Clear All
        </button>
      </div>

      {/* Status Message */}
      {message && (
        <div className={`message ${message.type}`}>
          <MaterialIcon icon={message.type === 'success' ? 'check_circle' : 'error'} size={18} />
          <span>{message.text}</span>
        </div>
      )}
    </div>
  );
}

PriorityTagsManager.propTypes = {};

export default PriorityTagsManager;
